/**
 * What a Run pressed by hand has to wait for.
 *
 * The backend names it - `stats.run_held_by` on a job, `job_run_held_by` on the
 * task page: the roster task while it is in flight, since the ranges it deals out
 * are not final before it ends, and otherwise the task of the job still logging in
 * or collecting the roster, the one start_task refuses a start behind.
 */
import type { BotsManagementWsEvent } from "features/ws/botsManagementWs";

export type RunHeldBy = {
  id: number;
  roster_only: boolean;
  task_status: string;
  bot_work_status: string;
  /** The task's number on the panel row; null for the roster task. */
  index_in_job: number | null;
};

// services._QUEUE_HOLD_TASK_STATUSES / _QUEUE_HOLD_BOT_STATUSES.
const HOLD_TASK_STATUSES = ["ECS_CONTAINER_STARTED", "LOGGED_IN", "PROCESSING"];
const HOLD_BOT_STATUSES = [
  "NOT_LOGGED_IN",
  "2FA",
  "CAPTCHA",
  "LOGGED_IN",
  "COLLECTING",
];

const PHASE_TEXT: Record<string, string> = {
  NOT_LOGGED_IN: "logging in",
  "2FA": "waiting for a 2FA code",
  CAPTCHA: "waiting on a captcha",
  LOGGED_IN: "collecting the roster",
  COLLECTING: "collecting the roster",
};

/** The roster task's own Run, and Build roster only, while the job is working. */
export const ROSTER_RUN_BLOCKED_REASON =
  "The job has a queued or running task - wait for it to finish, or stop it, " +
  "before building the roster.";

export function parseRunHeldBy(raw: unknown): RunHeldBy | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = Number(o.id);
  if (!Number.isInteger(id)) return null;
  const index = o.index_in_job == null ? NaN : Number(o.index_in_job);
  return {
    id,
    roster_only: o.roster_only === true,
    task_status: String(o.task_status ?? "").toUpperCase(),
    bot_work_status: String(o.bot_work_status ?? "").toUpperCase(),
    index_in_job: Number.isInteger(index) ? index : null,
  };
}

/** Run all, and Run on every task, while the roster task is in flight. */
export function rosterBuildReason(isUpload: boolean): string {
  const what = isUpload
    ? "building the Matches files"
    : "collecting the roster";
  return (
    `The roster task (R) is ${what}, and the task ranges follow from it. The ` +
    "tasks can be started once it finishes - to stop it, press Stop on the job's R row."
  );
}

/** Why this task's Run is greyed out, or null when it may start. */
export function runHeldReason(
  heldBy: RunHeldBy | null | undefined,
  taskId: number,
  isUpload: boolean,
): string | null {
  if (!heldBy || heldBy.id === taskId) return null;
  if (heldBy.roster_only) return rosterBuildReason(isUpload);
  const who =
    heldBy.index_in_job != null
      ? `Task ${heldBy.index_in_job}`
      : `Task #${heldBy.id}`;
  const phase = PHASE_TEXT[heldBy.bot_work_status] ?? "in its login phase";
  return `${who} of this job is ${phase} - Run opens once it is processing.`;
}

export type RunHoldWatch = { jobId: number | null; heldById: number | null };

/** A task event that can change what a Run waits for: the holder's own, or a
 * sibling's that enters the login phase. Progress and queueing do not. */
export function isRunHoldEvent(
  event: BotsManagementWsEvent,
  watch: RunHoldWatch,
): boolean {
  const task = event.task;
  if (task?.task_id == null) return false;
  if (watch.heldById != null && task.task_id === watch.heldById) return true;
  if (watch.jobId == null || task.job_id !== watch.jobId) return false;
  return (
    HOLD_TASK_STATUSES.includes(String(task.task_status ?? "").toUpperCase()) &&
    HOLD_BOT_STATUSES.includes(String(task.bot_work_status ?? "").toUpperCase())
  );
}

/**
 * What a job waits for one level up: a sibling of the same major job holds the
 * last slot (`stats.major_job_hold`); null when nothing does. `waiting` is true
 * only for a job with tasks in line - an idle one is held back too.
 */
export type MajorJobHold = {
  limit: number | null;
  running_jobs: { id: number; name: string }[];
  waiting: boolean;
};

export function parseMajorJobHold(raw: unknown): MajorJobHold | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const rows = Array.isArray(o.running_jobs) ? o.running_jobs : [];
  const running = rows
    .map((row) => {
      const item = (row ?? {}) as Record<string, unknown>;
      const id = Number(item.id);
      if (!Number.isInteger(id)) return null;
      return { id, name: String(item.name ?? `job #${id}`) };
    })
    .filter((row): row is { id: number; name: string } => row !== null);
  if (!running.length) return null;
  const limit = o.limit == null ? NaN : Number(o.limit);
  return {
    limit: Number.isInteger(limit) ? limit : null,
    running_jobs: running,
    waiting: o.waiting === true,
  };
}

/** Why this job cannot start yet, or null when nothing holds it back. */
export function majorJobHoldReason(
  hold: MajorJobHold | null | undefined,
): string | null {
  if (!hold) return null;
  const names = hold.running_jobs.map((row) => row.name).join(", ");
  const verb = hold.running_jobs.length === 1 ? "is" : "are";
  const count =
    hold.limit == null
      ? `${hold.running_jobs.length}`
      : `${hold.running_jobs.length} of ${hold.limit}`;
  const pace =
    hold.limit == null
      ? "a limited number of jobs"
      : `${hold.limit} job${hold.limit === 1 ? "" : "s"}`;
  if (hold.waiting) {
    return (
      `Waiting: ${names} ${verb} running, ${count}. This major job runs ${pace} ` +
      "at a time - this one starts by itself when a slot frees up."
    );
  }
  return (
    `${names} ${verb} running, ${count}. This major job runs ${pace} at a time - ` +
    "Run all puts this job in line, and it starts by itself when a slot frees up."
  );
}
